import React, { useState } from "react";
import {
  Box,
  TableRow,
  TableCell,
  Typography,
  Button,
  Modal,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const tdLabelSx = {
  width: 380,
  bgcolor: "#f3f3f3",
  borderRight: "1px solid #e0e0e0",
  color: "#333",
  fontWeight: 700,
  verticalAlign: "top",
};

const tdFieldSx = { p: 1.5, verticalAlign: "middle" };
const noteSx = { color: "#666", fontSize: 12, lineHeight: 1.25 };

export default function EmissionAndSequenceSettings({
  puntos = [],
  secuenciales = [],
  onRefresh,
  getCSRF,
  setSnack: externalSetSnack,
}) {
  const [snack, setSnackLocal] = useState({ open: false, msg: "", type: "success" });
  const setSnack = typeof externalSetSnack === "function" ? externalSetSnack : setSnackLocal;

  const [puntoOpen, setPuntoOpen] = useState(false);
  const [puntoForm, setPuntoForm] = useState({
    tipo_documento: "FACTURA",
    establecimiento: "001",
    punto_emision: "001",
  });

  const [secuencialOpen, setSecuencialOpen] = useState(false);
  const [secForm, setSecForm] = useState({
    tipo_documento: "FACTURA",
    establecimiento: "001",
    punto_emision: "001",
    secuencia_inicial: "000000001",
  });

  const addPunto = async () => {
    try {
      const fd = new FormData();
      fd.append("tipo_documento", puntoForm.tipo_documento);
      fd.append("establecimiento", "001");
      fd.append("punto_emision", puntoForm.punto_emision.padStart(3, "0"));

      const r = await fetch("/facturas/puntos/agregar/", {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": getCSRF() },
        body: fd,
      });

      const j = await r.json();
      if (r.ok) {
        setSnack({ open: true, type: "success", msg: "Punto agregado" });
        setPuntoOpen(false);
        await onRefresh();
      } else {
        setSnack({ open: true, type: "error", msg: j.error || "No se pudo agregar" });
      }
    } catch {
      setSnack({ open: true, type: "error", msg: "Error de conexión" });
    }
  };

  const deletePunto = async (item) => {
    try {
      const r = await fetch("/facturas/puntos/eliminar/", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": getCSRF() },
        body: JSON.stringify({
          id: item.id,
          tipo_documento: item.tipo_documento,
          establecimiento: item.establecimiento,
          punto_emision: item.punto_emision,
        }),
      });

      const j = await r.json();
      if (r.ok) {
        setSnack({ open: true, type: "success", msg: "Punto eliminado" });
        await onRefresh();
      } else {
        setSnack({ open: true, type: "error", msg: j.error || "No se pudo eliminar" });
      }
    } catch {
      setSnack({ open: true, type: "error", msg: "Error de conexión" });
    }
  };

  const addSecuencial = async () => {
    try {
      const fd = new FormData();
      fd.append("tipo_documento", "FACTURA");
      fd.append("establecimiento", "001");
      fd.append("punto_emision", secForm.punto_emision.padStart(3, "0"));
      fd.append("secuencia_inicial", String(parseInt(secForm.secuencia_inicial || "1", 10)));

      const r = await fetch("/facturas/secuenciales/actualizar/", {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": getCSRF() },
        body: fd,
      });

      const j = await r.json();
      if (r.ok) {
        setSnack({ open: true, type: "success", msg: "Secuencia modificada" });
        setSecuencialOpen(false);
        await onRefresh();
      } else {
        setSnack({ open: true, type: "error", msg: j.error || "No se pudo modificar" });
      }
    } catch {
      setSnack({ open: true, type: "error", msg: "Error de conexión" });
    }
  };

  const deleteSecuencial = async (item) => {
    try {
      const r = await fetch("/facturas/secuenciales/actualizar/", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": getCSRF() },
        body: JSON.stringify({
          eliminar: 1,
          tipo_documento: item.tipo_documento,
          establecimiento: item.establecimiento,
          punto_emision: item.punto_emision,
        }),
      });

      const j = await r.json();
      if (r.ok) {
        setSnack({ open: true, type: "success", msg: "Secuencia eliminada" });
        await onRefresh();
      } else {
        setSnack({ open: true, type: "error", msg: j.error || "No se pudo eliminar" });
      }
    } catch {
      setSnack({ open: true, type: "error", msg: "Error de conexión" });
    }
  };

  return (
    <>
      <TableRow>
        <TableCell sx={tdLabelSx}>
          Establecimiento - Punto Emisión por defecto
          <Box sx={noteSx}>(Válido para registro manual de documentos)</Box>
        </TableCell>
        <TableCell sx={tdFieldSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setPuntoOpen(true)} sx={{ textTransform: "none" }}>
              Añadir
            </Button>
            {puntos.map((p) => (
              <Box key={`${p.tipo_documento}-${p.establecimiento}-${p.punto_emision}`} sx={{ display: "inline-flex", alignItems: "center", gap: 1, px: 1.25, py: 0.75, border: "1px solid #e0e0e0", borderRadius: 1, bgcolor: "#fafafa" }}>
                <Typography>
                  {p.tipo_documento === "FACTURA" ? "Factura" : p.tipo_documento} {p.establecimiento}-{p.punto_emision}
                </Typography>
                <Tooltip title="Eliminar">
                  <IconButton size="small" onClick={() => deletePunto(p)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell sx={tdLabelSx}>Secuencia inicial de Documentos Electrónicos</TableCell>
        <TableCell sx={tdFieldSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setSecuencialOpen(true)} sx={{ textTransform: "none" }}>
              Modificar
            </Button>
            {secuenciales.map((s) => (
              <Box key={`${s.tipo_documento}-${s.establecimiento}-${s.punto_emision}`} sx={{ display: "inline-flex", alignItems: "center", gap: 1, px: 1.25, py: 0.75, border: "1px solid #e0e0e0", borderRadius: 1, bgcolor: "#fafafa" }}>
                <Typography>
                  {s.tipo_documento === "FACTURA" ? "Factura" : s.tipo_documento} {s.establecimiento}-{s.punto_emision}-{String(s.secuencia_inicial).padStart(9, "0")}
                </Typography>
                <Tooltip title="Eliminar">
                  <IconButton size="small" onClick={() => deleteSecuencial(s)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        </TableCell>
      </TableRow>

      <Modal open={puntoOpen} onClose={() => setPuntoOpen(false)}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", bgcolor: "#fff", p: 3, borderRadius: 2, width: 640, boxShadow: 24 }}>
          <Typography sx={{ fontWeight: 800, mb: 2 }}>Establecimiento - Punto de Emisión por Defecto</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
            <FormControl>
              <InputLabel>Tipo Documento</InputLabel>
              <Select label="Tipo Documento" value={puntoForm.tipo_documento} onChange={(e) => setPuntoForm((p) => ({ ...p, tipo_documento: e.target.value }))}>
                <MenuItem value="FACTURA">Factura</MenuItem>
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel>Establecimiento</InputLabel>
              <Select label="Establecimiento" value="001">
                <MenuItem value="001">001</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Punto Emisión" value={puntoForm.punto_emision} onChange={(e) => setPuntoForm((p) => ({ ...p, punto_emision: e.target.value.replace(/\D/g, "").slice(0, 3) }))} />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
            <Button onClick={() => setPuntoOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={addPunto}>Aceptar</Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={secuencialOpen} onClose={() => setSecuencialOpen(false)}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", bgcolor: "#fff", p: 3, borderRadius: 2, width: 680, boxShadow: 24 }}>
          <Typography sx={{ fontWeight: 800, mb: 2 }}>Secuencia inicial de Documentos Electrónicos</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 2fr", gap: 2 }}>
            <FormControl>
              <InputLabel>Tipo Documento</InputLabel>
              <Select label="Tipo Documento" value="FACTURA">
                <MenuItem value="FACTURA">Factura</MenuItem>
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel>Establecimiento</InputLabel>
              <Select label="Establecimiento" value="001">
                <MenuItem value="001">001</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Punto Emisión" value={secForm.punto_emision} onChange={(e) => setSecForm((p) => ({ ...p, punto_emision: e.target.value.replace(/\D/g, "").slice(0, 3) }))} />
            <TextField label="Secuencial Inicial" value={secForm.secuencia_inicial} onChange={(e) => setSecForm((p) => ({ ...p, secuencia_inicial: e.target.value.replace(/\D/g, "").slice(0, 9) }))} />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
            <Button onClick={() => setSecuencialOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={addSecuencial}>Aceptar</Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.type} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
